import {
    Component,
    Element,
    h,
    Host,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { MaterialIconVariant } from '../../models/portalActions';
import store from '../../store/store';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-icon-button',
    styleUrl: 'portal-icon-button.scss',
    shadow: true,
})
export class PortalIconButton {
    @Element() el: HTMLPortalIconButtonElement;

    /**
   * 'disabled' property
   * @memberof PortalIconButton
   */

    @Prop() disabled: boolean;

    /**
   * background Remove removes the background styling
   * @memberof PortalIconButton
   */
    @Prop() type: 'button' | 'icon' = 'button';

    /**
  * name of the icon
  * @memberof PortalIconButton
  */
    @Prop() name: string;

    /**
   * Aria Label of the icon
   * @memberof PortalIconButton
   */
    @Prop() ariaLabel: ARIAMixin['ariaLabel'];

    /**
   * Variation of the Icon
   * @memberof PortalIconButton
   */
    @Prop() variant: MaterialIconVariant = 'outlined';

    /**
  * `haspopup` accessibility attribute. Pass this if the button will open a menu/popup
  * @memberof PortalIconButton
  */
    @Prop() haspopup: boolean = false;

    /**
  * `expanded` accessibility attribute
  * @memberof PortalIconButton
  */
    @Prop() expanded: boolean = false;

    /**
   * `controls` accessibility attribute
   * @memberof PortalIconButton
   */
    @Prop() controls: string;

    /**
   * `describedBy` accessibility attribute
   * @memberof PortalIconButton
   */
    @Prop() describedBy: string;

    /**
  * `cssClass` property of the button
  * @memberof PortalIconButton
  */
    @Prop() cssClass: string = '';

    /**
   * `size` property of the button
   * @memberof PortalIconButton
   */
    @Prop() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'sm';

    /**
   * `color` property of the button
   * @memberof PortalIconButton
   */
    @Prop() color: string = '';

    /**
  * Theme override to bypass store state
  * @memberof PortalIconButton
  */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
   * Icon size of the button
   * @memberof PortalIconButton
   */
    @Prop() iconSize: string;

    /**
     * Shows visual focus
     */
    @Prop() showVisualFocus: boolean = true;

    render() {
        const portalIcon = 'portal-icons';
        const classes = {
            [this.cssClass]: true,
            [this.type]: true,
            [this.color]: true,
            [portalIcon]: true,
            [this.size]: true,
            'transparent': this.type === 'icon',
            'hideFocus': !this.showVisualFocus,
        };

        const props = {
            'aria-haspopup': `${this.haspopup}`,
            'aria-expanded': `${this.expanded}`,
            'aria-controls': this.controls,
            'aria-describedby': this.describedBy,
            'disabled': this.disabled,
            'color': this.color,
            'type': this.type,
        };
        return (
            <Host aria-label={this.ariaLabel} class={getTheme(store, this.theme)} >
                <button {...props} aria-label={this.ariaLabel} class={classes}>
                    <portal-icon
                        name={this.name}
                        size={this.iconSize || this.size}
                        variant={this.variant}
                    />
                </button>
            </Host>
        );
    }
}
